import React, { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { CarouselProvider, Slider, Slide, ButtonBack, ButtonNext } from 'pure-react-carousel';

import { constants } from '@nhl/formatter-utilities';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay'
import useInterval from '@use-it/interval';

import gameStateTypes from '../../../utils/gameStateTypes';
import { gameListOperations } from '../../../redux/ducks/gameList';
import { seasonSelectors } from '../../../redux/ducks/seasons';

import { HSBGameDisplay, HSBDateBlock, PrevArrow, NextArrow } from './components';
import ComponentSeasonIdentifiers from '../../../utils/componentSeasonIdentifiers';

import 'pure-react-carousel/dist/react-carousel.es.css';
import './styles.scss';

const INTERVAL_UPDATE_FREQUENCY = 20000;
let dateRangeLowerBound;
let dateRangeUpperBound;
const PARTY_KEY = Math.random() + '_headerScoreboard';

const isEqual = ({ game }) => {
    if (game === undefined) {
        return true;
    } else {
        const { state } = game;
        return state.id < gameStateTypes.PREGAME || state.id >= gameStateTypes.OFFICIAL;
    }
}

const MemoizedSlide = React.memo(({index, currentDate, game, isAccountApp}) => {
    if (game === undefined) {
        return (
            <Slide index={index}>
                <HSBDateBlock date={currentDate} />
            </Slide>
        );
    } else {
        return <Slide index={index}><HSBGameDisplay game={game} isAccountApp={isAccountApp}/></Slide>
    }
}, isEqual);

const HeaderScoreboard = ({ isAccountApp }) => {
    const dispatch = useDispatch();
    const isLoading = useSelector(({ gameList }) => gameList.loadingState.partiesLoading.includes(PARTY_KEY));
    const hasLoaded = useSelector(({ gameList }) => gameList.loadingState.partiesHaveLoaded.includes(PARTY_KEY));
    const gameData = useSelector(({ gameList }) => gameList.gameListState.headerGameData);
    const retainBoxscoreFeedDataId = useSelector(({ gameList }) => gameList.gameListState.retainBoxscoreFeedDataId);
    const gameDate = useSelector(({ gameList }) => gameList.gameListState.gameDate);
    const currentSeason = seasonSelectors(ComponentSeasonIdentifiers.GAMES);

    const [initialLoadStarted, setInitialLoadStarted] = useState(false);
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const [autoUpdateInterval, setAutoUpdateInterval] = useState(null);

    const isDayAfterSeasonEnd = day => moment(currentSeason.endDate, constants.DATE_FORMAT).isBefore(day, 'day');

    const isDayBeforeSeasonStart = day => moment(currentSeason.startDate, constants.DATE_FORMAT).isAfter(day, 'day');

    const generateGameList = games => {
        const list = [];
        let hasActiveGame = false;
        let currentDateIndex = null;
        let currentDate = null;
        let closestDateToServerTimeAvailable = null;
        let closestDateIndex = null;

        games.forEach(game => {
            const dateOfGame = moment(game.gameDate, constants.DATE_FORMAT);

            if (dateOfGame.isBetween(dateRangeLowerBound, dateRangeUpperBound, null, '[)')) {
                if (!dateOfGame.isSame(currentDate)) {
                    currentDate = dateOfGame;
                    list.push(
                        <MemoizedSlide key={currentDate.format(constants.DATE_FORMAT)} index={list.length} currentDate={currentDate} />
                    );

                    const currentIndex = list.length - 1;

                    if (gameDate.isSame(currentDate, 'day')) {
                        currentDateIndex = currentIndex;
                    }

                    if (currentDateIndex === null) {
                        if (closestDateToServerTimeAvailable === null || (Math.abs(gameDate.diff(currentDate)) <
                                Math.abs(gameDate.diff(closestDateToServerTimeAvailable)))) {
                            closestDateToServerTimeAvailable = currentDate;
                            closestDateIndex = currentIndex;
                        }
                    }
                }

                list.push(
                    <MemoizedSlide key={game.id} index={list.length} game={game} isAccountApp={isAccountApp} />
                );

                if (game.state.id >= gameStateTypes.PREGAME && game.state.id < gameStateTypes.HARD_FINAL) {
                    hasActiveGame = true;
                }
            }
        });

        if (hasActiveGame && autoUpdateInterval === null) {
            setAutoUpdateInterval(INTERVAL_UPDATE_FREQUENCY);
        } else if (!hasActiveGame && autoUpdateInterval !== null) {
            setAutoUpdateInterval(null);
        }

        return {
            initialSlideIndex: (currentDateIndex !== null ? currentDateIndex : closestDateIndex),
            list
        };
    };

    const shouldShowControls = () => window.innerWidth >= 850;

    const getSettingsBasedOnWindowWidth = (slideData) => {
        const settings = {
            visibleSlides: 1,
            dragEnabled: !shouldShowControls()
        };

        // Calculate how many slides to show based on how many ~160px divs we can show
        // If this device is wide enough to where we should show controls, account for the width of those
        // controls in the calculation. Otherwise, just divide the slide width by the current innerWidth of the window
        const HSB_CONTROL_PADDING = 80 + 40;
        const HSB_SLIDE_WIDTH = 160;
        const windowWidth = window.innerWidth;

        settings.visibleSlides = shouldShowControls() ?
            Math.floor((windowWidth - HSB_CONTROL_PADDING) / HSB_SLIDE_WIDTH) :
            Math.floor(windowWidth / HSB_SLIDE_WIDTH);
        settings.step = settings.visibleSlides / 2;
        settings.dragStep = settings.step;
        settings.currentSlide = slideData.list.length <= settings.visibleSlides ? 0 : slideData.initialSlideIndex;

        return settings;
    };

    const throttle = (func, wait) => {
        let timeout;
        return () => {
            let later = () => {
                timeout = null;
                func.apply(this);
            };
            let callNow = !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
            if (callNow) func.apply(this);
        };
    };

    useInterval(() => {
        dispatch(gameListOperations.fetchGameDataByDateRange(
            dateRangeLowerBound, dateRangeUpperBound, currentSeason.id, PARTY_KEY, retainBoxscoreFeedDataId, true));
    }, autoUpdateInterval);

    // emulating a forceUpdate:
    const  [,setState] = useState();
    const handleUpdate = () => {
        //passing empty object will re-render the component
        setState({});
    };

    // If the retainBoxscoreFeedDataId is updated we need to clear the timer then start it again.
    // So when we dispatch fetchGameDataByDateRange we will provide the correct retainBoxscoreFeedDataId
    useEffect(() => {
        setAutoUpdateInterval(null);
    }, [retainBoxscoreFeedDataId]);

    useEffect(() => {
        dispatch(gameListOperations.fetchGameDate(PARTY_KEY));
        window.addEventListener('resize', throttle(handleUpdate, 100));
        return () => {
            setAutoUpdateInterval(null);
            window.removeEventListener('resize', null);
        }
    }, []);

    useEffect(() => {
        setInitialLoadStarted(false);
        setInitialLoadDone(true);
    }, [isLoading, hasLoaded]);

    useEffect(() => {
        if (gameDate && !gameData.length) {
            if (!initialLoadStarted) {
                setInitialLoadStarted(true);
                setInitialLoadDone(false);
            }

            const isPlayoffs = moment(currentSeason.regularSeasonEndDate, constants.DATE_FORMAT).isBefore(gameDate, 'day') &&
                moment(currentSeason.endDate, constants.DATE_FORMAT).isAfter(gameDate, 'day');

            if (isDayAfterSeasonEnd(gameDate)) {
                dateRangeUpperBound = moment(currentSeason.endDate, constants.DATE_FORMAT).add(1, 'days');
                dateRangeLowerBound = moment(currentSeason.endDate, constants.DATE_FORMAT).subtract(1, 'month');
            } else if (isDayBeforeSeasonStart(gameDate)) {
                dateRangeLowerBound = moment(currentSeason.startDate, constants.DATE_FORMAT).subtract(1, 'month').startOf('month');
                dateRangeUpperBound = moment(currentSeason.startDate, constants.DATE_FORMAT).add(8, 'days');
            } else {
                dateRangeLowerBound = gameDate.clone().subtract(7, 'days');
                // For playoffs, we set the end date as the season end date so that we show all games that are available
                // We will probably want to rework this, or entirely replace the api call to align with freshice
                dateRangeUpperBound = gameDate.clone().add(isPlayoffs ? 15 : 8, 'days');
            }

            dispatch(gameListOperations.fetchGameDataByDateRange(dateRangeLowerBound, dateRangeUpperBound, currentSeason.id,
                PARTY_KEY, retainBoxscoreFeedDataId, true));
        }
    }, [gameDate, gameData]);

    const slideData = ((!isLoading || initialLoadDone) && gameDate) ? generateGameList(gameData) : {list: []};

    return (
        <LoadingDisplay isLoading={isLoading && !initialLoadDone}>{
            slideData.list.length > 0 &&
                <div className="header-scoreboard">
                    <CarouselProvider
                        naturalSlideWidth={160}
                        naturalSlideHeight={80}
                        totalSlides={slideData.list.length}
                        {...getSettingsBasedOnWindowWidth(slideData)}
                    >
                        <Slider className={`hsb-slider ${shouldShowControls() ? '' : 'no-controls'}`}>
                            {slideData.list}
                        </Slider>
                        {shouldShowControls() &&
                            <Fragment>
                                <ButtonBack className="hsb-nav-block prev">
                                    <PrevArrow/>
                                </ButtonBack>
                                <ButtonNext className="hsb-nav-block next">
                                    <NextArrow isAccountApp={isAccountApp}/>
                                </ButtonNext>
                            </Fragment>
                        }
                    </CarouselProvider>
                </div>
        }</LoadingDisplay>
    );
};

export default HeaderScoreboard;
